import React, { type KeyboardEvent, useCallback, useState, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button, { type ButtonProps } from '@atlaskit/button';
import DropdownMenu, {
	type OnOpenChangeArgs,
	type CustomTriggerProps,
	DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { THIRD_PARTY_HIERARCHY_SELECTOR_TEST_ID_PREFIX } from '../../../../common/constants';
import type { Field } from '../../../../common/constants/fields';
import { FieldSelectorItem } from '../../../../common/ui/field-selector-item';
import { getIcon } from '../../../../common/utils/fields';

type AvailableItem = Field & { isDisabled: boolean };

export type Props = {
	item: Field;
	mappedItem: Field;
	availableItems: AvailableItem[];
	isDisabled: boolean;
	onClick?: (selectedItem: Field) => void;
	indentType: 'large' | 'small' | 'default';
	disabledFieldTooltip?: string;
};

export const ItemSelector = ({
	mappedItem,
	availableItems,
	isDisabled,
	onClick,
	indentType,
	disabledFieldTooltip,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);

	const toggleMenuOpenState = useCallback((attrs: OnOpenChangeArgs) => {
		setIsOpen(attrs.isOpen);
	}, []);

	const onKeyDownHandler = useCallback((event: KeyboardEvent<HTMLElement>) => {
		if (event.key === 'Tab') {
			event.stopPropagation();
		}
	}, []);

	const handleDropdownItemClickFactory = useCallback(
		(item: Field) => () =>
			onClick &&
			onClick({
				fieldType: item.fieldType,
				fieldName: item.fieldName,
				fieldId: item.fieldId,
			}),
		[onClick],
	);

	const availableOptions = useMemo(
		() =>
			availableItems.map((item) => {
				const isSelected = !!mappedItem?.fieldId && item.fieldId === mappedItem.fieldId;
				return (
					<FieldSelectorItem
						key={`${item.fieldName}-${item.fieldType}`}
						name={item.fieldName}
						icon={getIcon({
							fieldType: item.fieldType,
							isSelected,
							isDisabled: item.isDisabled,
						})}
						onClick={handleDropdownItemClickFactory(item)}
						isDisabled={item.isDisabled}
						isSelected={isSelected}
						tooltipContent={disabledFieldTooltip}
					/>
				);
			}),
		[availableItems, disabledFieldTooltip, handleDropdownItemClickFactory, mappedItem.fieldId],
	);

	const dropdownTrigger = useCallback(
		({ triggerRef, isSelected, ...props }: CustomTriggerProps<HTMLElement>) => (
			<StyledButton
				{...props}
				iconAfter={isDisabled ? undefined : <ChevronDownIcon label="" size="large" />}
				ref={triggerRef}
				isOpen={isSelected}
				isDisabled={isDisabled}
			>
				<Flex alignItems="center" gap="space.150">
					{getIcon({
						fieldType: mappedItem.fieldType,
						isDisabled,
						isSubtle: !isDisabled,
					})}
					<span>{mappedItem.fieldName}</span>
				</Flex>
			</StyledButton>
		),
		[isDisabled, mappedItem.fieldName, mappedItem.fieldType],
	);

	return (
		<Box xcss={styleMap[indentType]} testId={THIRD_PARTY_HIERARCHY_SELECTOR_TEST_ID_PREFIX}>
			<DropdownMenu
				isOpen={isOpen}
				onOpenChange={toggleMenuOpenState}
				shouldFlip
				trigger={dropdownTrigger}
			>
				<Box onKeyDown={onKeyDownHandler} xcss={DropdownItemStyles}>
					<DropdownItemGroup
						testId={`${THIRD_PARTY_HIERARCHY_SELECTOR_TEST_ID_PREFIX}.dropdown-item-group`}
					>
						{availableOptions}
					</DropdownItemGroup>
				</Box>
			</DropdownMenu>
		</Box>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)<
	ButtonProps & {
		isOpen?: boolean;
	}
>({
	'&&': {
		width: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: ({ isOpen }) => (isOpen ? token('color.text', colors.N900) : 'inherit'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		outline: ({ isOpen }) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			isOpen ? `2px solid ${token('color.border.focused', colors.B100)}` : 'none',
		// this focus styling does not do anything but reconcile missing focus styles in AKButton possibly related to #hot-103234
		':focus': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			outline: `2px solid ${token('color.border.focused', colors.B100)}`,
		},
		textTransform: 'capitalize',
	},
});

const styleMap = {
	small: xcss({ marginLeft: 'space.150' }),
	large: xcss({ marginLeft: 'space.300' }),
	default: xcss({ marginLeft: 'space.0' }),
};

const DropdownItemStyles = xcss({
	width: '280px',
});
